import { computed } from "vue";
/**
 * Evaluates a FieldRule against block-scoped values and returns a reactive `isVisible` boolean.
 *
 * Visibility resolution:
 *   true (or absent)      → always visible (default)
 *   false                 → always hidden
 *   VisibilityCondition   → evaluated against the provided block values
 *
 * Block-scoped: `values` should only contain fields within the same block (ADR-005).
 */
export function useFieldVisibility(rule, values) {
    const isVisible = computed(() => evaluateFieldVisibility(rule, values.value));
    return { isVisible };
}
/**
 * Standalone (non-reactive) evaluator — use this when you need to evaluate
 * visibility for multiple fields at once (e.g. inside a computed in OrderedBlock).
 */
export function evaluateFieldVisibility(rule, values) {
    if (!rule)
        return true;
    const { visible } = rule;
    if (visible == null)
        return true;
    if (typeof visible === "boolean")
        return visible;
    return evaluateCondition(visible, values);
}
function evaluateCondition(condition, values) {
    const fieldValue = values[condition.field];
    switch (condition.operator) {
        case "notEmpty":
            return !isEmpty(fieldValue);
        case "empty":
            return isEmpty(fieldValue);
        case "equals":
            return fieldValue === condition.value;
        case "notEquals":
            return fieldValue !== condition.value;
        case "contains": {
            if (typeof fieldValue === "string" && typeof condition.value === "string") {
                return fieldValue.includes(condition.value);
            }
            if (Array.isArray(fieldValue)) {
                return fieldValue.includes(condition.value);
            }
            return false;
        }
        default:
            return true;
    }
}
function isEmpty(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === "string")
        return value.trim() === "";
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
